<script>
  import { onMount, setContext, afterUpdate, beforeUpdate } from 'svelte';
  import * as d3 from 'd3';
  import * as L from 'leaflet';

  import {
    floodDomain,
    heatDomain,
    wildfireDomain,
    activeFacilityIds,
    floodColorScale,
    heatColorScale,
    wildfireColorScale
  } from './stores';

  export let geojson;
  export let factorKey;
  export let highlightFacility;
  export let centroids;
  export let setFacility;
  export let reboundMap;

  let container;
  let map;
  let dataLayer;
  let hiddenLayer = L.featureGroup();
  let highlightBounds;
  let facilityLeafletMap = {};
  let leafletFacilityMap = {};

  onMount(() => {
    /*
    [43.052, 86.212]
    nyc - [40.735707, -73.990963]
    center us- [40.23480648685492, -99.14089442776677]
    */

    map = L.map(container, {}).setView(
      [40.23480648685492, -99.14089442776677],
      5
    );

    /*
      ESRI World Imagery https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}
      CartoDB https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png
      OpenTopo https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png
      local /public/osm-tiles/{z}/{x}/{y}.png
    */
    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      {
        attribution:
          'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
        maxZoom: 20
      }
    ).addTo(map);

    // updateMap();

    map.on('zoom', () => {
      // console.log(`current zoom: ${map.getZoom()}`);
      // console.log(`zoom bounds: ${JSON.stringify(map.getBounds())}`);
      // clearMap();
      updateMap(false);
    });

    // map.on('moveend', () => {
    //   console.log(`moveend bounds: ${JSON.stringify(map.getBounds())}`);
    // });

    // map.on('zoomend', () => {
    //   console.log(`zoomend bounds: ${JSON.stringify(map.getBounds())}`);
    // });

    return () => {
      map.remove();
    };
  });

  function clearMap() {
    // console.log(`clearMap`);
    if (dataLayer) {
      removeFeatures();
      removeDataLayer();
      dataLayer = null;
      highlightBounds = null;
    }
  }

  afterUpdate(() => {
    // console.log('afterUpdate');
    // console.log(reboundMap);
    // clearMap();
    updateMap(reboundMap);
  });

  function removeFeatures() {
    dataLayer.eachLayer((l) => {
      l.removeFrom(map);
    });
    dataLayer.clearLayers();
  }

  function removeDataLayer() {
    dataLayer.remove();
  }

  function getDomain() {
    switch (factorKey) {
      case 'flood_risk_rating':
        return $floodDomain;
      case 'heat_risk_rating':
        return $heatDomain;
      case 'wildfire_risk_rating':
        return $wildfireDomain;
      default:
        return [1, 1];
    }
  }

  function getColor(factorKey) {
    switch (factorKey) {
      case 'flood_risk_rating':
        return 'blue';
      case 'heat_risk_rating':
        return 'orange';
      case 'wildfire_risk_rating':
        return 'red';
      default:
        return 'blue';
    }
  }

  function getColorScale(factorKey) {
    switch (factorKey) {
      case 'flood_risk_rating':
        return floodColorScale;
      case 'heat_risk_rating':
        return heatColorScale;
      case 'wildfire_risk_rating':
        return wildfireColorScale;
      default:
        return floodColorScale;
    }
  }

  //todo: tweak ranges for zoom levels
  function getRange(mapZoom) {
    if (mapZoom <= 5) {
      return [5000, 25000];
    } else if (mapZoom > 5 && mapZoom <= 8) {
      return [3500, 15000];
    } else if (mapZoom > 8 && mapZoom <= 10) {
      return [2000, 9500];
    } else if (mapZoom > 10 && mapZoom <= 13) {
      return [1250, 6000];
    } else if (mapZoom > 13 && mapZoom <= 15) {
      return [500, 3500];
    } else {
      return [100, 1500];
    }
  }

  function scaleRadius(minRadius = 50, originalRadius, currentZoom) {
    return (
      1.0083 * Math.pow(originalRadius / minRadius, 0.5716) * (currentZoom / 2)
    );
  }

  function staticRadiusScale(mapZoom) {
    // console.log(mapZoom);
    switch (mapZoom) {
      case 1:
        return 7000;
      case 2:
        return 7000;
      case 3:
        return 7000;
      case 4:
        return 7000;
      case 5:
        return 7000;
      case 6:
        return 6000;
      case 7:
        return 5000;
      case 8:
        return 2500;
      case 9:
        return 1250;
      case 10:
        return 750;
      case 11:
        return 300;
      case 12:
        return 300;
      case 13:
        return 150;
      case 14:
        return 50;
      default:
        return 50;
    }
    // if (mapZoom <= 6) {
    //   console.log(mapZoom, 5000);
    //   return 5000;
    // } else if (mapZoom > 6 && mapZoom <= 9) {
    //   console.log(mapZoom, 2500);
    //   return 2500;
    // } else if (mapZoom > 9 && mapZoom <= 11) {
    //   console.log(mapZoom, 750);
    //   return 750;
    // } else if (mapZoom > 11 && mapZoom <= 13) {
    //   console.log(mapZoom, 150);
    //   return 150;
    //   // } else if (mapZoom > 13 && mapZoom <= 15) {
    //   //   return 200;
    //   // } else if (mapZoom > 15 && mapZoom <= 18) {
    //   //   return 100;
    // } else {
    //   console.log(mapZoom, 50);
    //   return 50;
    // }
  }

  function addBoundaries() {
    const fillColorScale = d3
      .scaleSequential(d3.schemeDark2)
      .domain(getDomain());

    return L.geoJSON(geojson, {
      onEachFeature: (f, l) => {
        l.on('click', () => {
          // console.log(
          //   `${f.properties.name} - ${factorKey}: ${f.properties[factorKey]}`
          // );
          setFacility(f.properties.facilityid);
        });
      },
      style: (f) => {
        return {
          stroke: true,
          weight: 1,
          fillColor: fillColorScale(f.properties[factorKey]),
          fillOpacity: 0.65,
          color: fillColorScale(f.properties[factorKey])
        };
      }
    });
  }

  function addCentroids() {
    // const radiusScale = d3
    //   .scaleLinear()
    //   .domain(getDomain())
    //   .range(getRange(map.getZoom()));

    // console.log(`map zoom`, map.getZoom());
    const radiusScale = (v) => {
      // return scaleRadius(50, , map.getZoom());
      return staticRadiusScale(map.getZoom());
    };

    return dataLayer
      ? updateDataLayer(radiusScale)
      : createDataLayer(radiusScale);
  }

  function isActive(facilityId) {
    return $activeFacilityIds.indexOf(facilityId) > -1;
  }

  function updateDataLayer(radiusScale) {
    hiddenLayer.eachLayer((l) => {
      if (!l.properties[factorKey] && l.properties[factorKey] !== 0) {
        l.setRadius(0);
      } else {
        l.setRadius(radiusScale(l.properties[factorKey]));
      }

      if (isActive(l.properties.facilityid)) {
        l.removeFrom(hiddenLayer);
        l.addTo(dataLayer);
      }
    });

    dataLayer.eachLayer((l) => {
      if (!l.properties[factorKey] && l.properties[factorKey] !== 0) {
        // console.log(
        //   l.properties.name,
        //   l.properties.facilityid,
        //   factorKey,
        //   l.properties[factorKey],
        //   radiusScale(l.properties[factorKey])
        // );
        l.setRadius(0);
      } else {
        l.setRadius(
          radiusScale(
            factorKey === 'flood_risk_rating' &&
              l.properties.confidence === 'low'
              ? 1
              : l.properties[factorKey]
          )
        );
        l.setStyle({
          fillColor:
            factorKey === 'flood_risk_rating' &&
            l.properties.confidence === 'low'
              ? 'grey'
              : getColorScale(factorKey)(l.properties[factorKey]),
          stroke: l.properties.facilityid === highlightFacility,
          color: 'black',
          weight: 5
        });
      }

      if (!isActive(l.properties.facilityid)) {
        l.removeFrom(dataLayer);
        l.addTo(hiddenLayer);
      }
    });

    return dataLayer;
  }

  function createDataLayer(radiusScale) {
    return L.featureGroup(
      geojson.features.map((f) => {
        const c = L.circle(
          [
            f.properties.centroid.coordinates[1],
            f.properties.centroid.coordinates[0]
          ],
          {
            radius: f.properties[factorKey]
              ? radiusScale(f.properties[factorKey])
              : radiusScale(0),
            stroke: true,
            weight: 1,
            fillOpacity: 0.75,
            strokeOpacity: 0.75,
            color: getColorScale(factorKey)(f.properties[factorKey]),
            fillColor: getColorScale(factorKey)(f.properties[factorKey])
          }
        );
        facilityLeafletMap[f.properties.facilityid] = c._leaflet_id;
        leafletFacilityMap[c._leaftlet_id] = f.properties.facilityid;
        c.on('click', () => {
          // console.log(
          //   `${f.properties.name} - ${factorKey}: ${f.properties[factorKey]}`
          // );
          setFacility(f.properties.facilityid);
        });
        c.properties = f.properties;
        return c;
      })
    );
  }

  function getHighlightBounds() {
    dataLayer.eachLayer((l) => {
      if (centroids) {
        if (l.properties.facilityid == highlightFacility) {
          highlightBounds = l.getBounds();
        }
      } else {
        if (l.feature.properties.facilityid == highlightFacility) {
          highlightBounds = l.getBounds();
        }
      }
    });
  }

  function updateMap(rebound = true) {
    // console.log('updateMap');
    // console.log(factorKey);
    // console.log(geojson.features[10]);

    dataLayer = centroids ? addCentroids() : addBoundaries();

    if (!map.hasLayer(dataLayer)) {
      dataLayer.addTo(map);
    }

    if (highlightFacility && rebound) {
      // console.log(highlightFacility);
      getHighlightBounds();
      map.fitBounds(highlightBounds, { maxZoom: 16 });
    } else if (rebound) {
      try {
        map.fitBounds(dataLayer.getBounds(), { maxZoom: 14, animate: true });
      } catch (error) {
        // console.log(error);
      }
      // map.setView([40.23480648685492, -99.14089442776677], 5);
    }
  }
</script>

<div bind:this={container}>
  {#if map}
    <slot />
  {/if}
</div>

<style>
  div {
    width: 100%;
    height: 45vh;
  }

  @media screen and (min-width: 30em) {
    div {
      height: 100vh;
    }
  }

  @media screen and (min-width: 30em) and (max-width: 60em) {
    div {
      height: 100vh;
    }
  }

  @media screen and (min-width: 60em) {
    div {
      height: 100vh;
    }
  }

  @media screen and (max-height: 550px) {
    div {
      height: 50vh !important;
      width: 100%;
    }
  }
</style>
